import { ShortcodeMatch } from '../type';

const getShortcodeRegex = (name: string) => {
  return new RegExp(
    `\\[(\\[?)(${name})(?![\\w-])([^\\]/]*(?:/(?!\\])[^\\]/]*)*?)(?:(/)\\]|\\]([^\\[]*)(?:\\[(?!/${name})\\][^\\[]*)*\\[/${name}\\])?(\\]?)`,
    'g',
  );
};
const regexpAttributes =
  /([\w-]+)\s*=\s*"([^"]*)"(?:\s|$)|([\w-]+)\s*=\s*'([^']*)'(?:\s|$)|([\w-]+)\s*=\s*([^\s'"]+)(?:\s|$)|"([^"]*)"(?:\s|$)|'([^']*)'(?:\s|$)|(\S+)(?:\s|$)/g;

/**
 * Finds and extracts shortcodes from a given text.
 *
 * This function searches for shortcodes in the provided text using a regular expression
 * that matches the specified shortcode name. It extracts the raw shortcode, its attributes,
 * and any content within the shortcode. It also determines if the shortcode is self-closing.
 *
 * @param text - The text to search for shortcodes.
 * @param name - The name of the shortcode to find (e.g., "price").
 * @returns An array of ShortcodeMatch objects, each representing a found shortcode,
 *          or null if no shortcodes are found.
 *
 * @example
 * const text = "Here is a [price size='large']$10[/price] shortcode.";
 * const shortcodes = findShortcode(text, 'price');
 * // shortcodes will contain an array with one ShortcodeMatch object
 */
export const findShortcode = (
  text: string,
  name: string,
): ShortcodeMatch[] | null => {
  const matches: ShortcodeMatch[] = [];
  let match;
  const regex = getShortcodeRegex(name);

  while ((match = regex.exec(text)) !== null) {
    const [raw, isEscaped, , attributesString, isSelfClosing, content] = match;
    const attributes: Record<string, string> = {};

    let attrMatch;
    while ((attrMatch = regexpAttributes.exec(attributesString)) !== null) {
      const [
        ,
        key1,
        value1,
        key2,
        value2,
        key3,
        value3,
        value4,
        value5,
        value6,
      ] = attrMatch;
      const key = key1 || key2 || key3;
      const value =
        value1 || value2 || value3 || value4 || value5 || value6 || '';
      if (key) {
        attributes[key] = value;
      }
    }

    matches.push({
      raw,
      isEscaped: !!isEscaped,
      name,
      attributes,
      isSelfClosing: !!isSelfClosing || !content,
      content: content || '',
    });
  }

  return matches.length > 0 ? matches : null;
};
